import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { leadsAPI, filePublicUrl, isImageAttachment } from '../../services/api';
import { HiOutlinePhone, HiOutlinePencil, HiOutlineUserAdd, HiOutlinePaperClip, HiOutlinePlusCircle, HiOutlinePlay, HiOutlineDownload, HiOutlineCalendar, HiOutlineCheckCircle, HiOutlineXCircle, HiOutlineExclamation } from 'react-icons/hi';
import { format } from 'date-fns';

const TYPE_CONFIG = {
  call: {
    icon: HiOutlinePhone,
    label: 'Call',
    bgColor: 'bg-green-100',
    textColor: 'text-green-600',
    lineColor: 'bg-green-500',
  },
  note: {
    icon: HiOutlinePencil,
    label: 'Note',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-600',
    lineColor: 'bg-blue-500',
  },
  assignment: {
    icon: HiOutlineUserAdd,
    label: 'Assignment',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-600',
    lineColor: 'bg-purple-500',
  },
  attachment: {
    icon: HiOutlinePaperClip,
    label: 'Attachment',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-600',
    lineColor: 'bg-amber-500',
  },
  created: {
    icon: HiOutlinePlusCircle,
    label: 'Lead Created',
    bgColor: 'bg-primary-100',
    textColor: 'text-primary-600',
    lineColor: 'bg-primary-500',
  },
  followup: {
    icon: HiOutlineCalendar,
    label: 'Follow-up',
    bgColor: 'bg-indigo-100',
    textColor: 'text-indigo-600',
    lineColor: 'bg-indigo-500',
  },
};

const CALL_STATUS_COLORS = {
  connected: 'bg-green-100 text-green-700',
  not_connected: 'bg-red-100 text-red-700',
  busy: 'bg-yellow-100 text-yellow-700',
  no_answer: 'bg-orange-100 text-orange-700',
  failed: 'bg-red-100 text-red-700',
  rejected: 'bg-red-100 text-red-700',
  missed: 'bg-orange-100 text-orange-700',
};

const formatDuration = (seconds) => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const CallEntry = ({ activity }) => {
  const statusColor = CALL_STATUS_COLORS[activity.status] || 'bg-gray-100 text-gray-700';

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusColor}`}>
          {activity.status?.replace('_', ' ')}
        </span>
        <span className="text-xs text-gray-500">
          {activity.callType === 'outgoing' ? 'Outgoing' : activity.callType === 'incoming' ? 'Incoming' : 'Missed'}
        </span>
        {activity.duration > 0 && (
          <span className="text-xs text-gray-400">· {formatDuration(activity.duration)}</span>
        )}
      </div>
      {activity.userName && (
        <p className="text-xs text-gray-500">By {activity.userName}</p>
      )}
      {activity.notes && (
        <p className="text-sm text-gray-700 mt-1">{activity.notes}</p>
      )}
      {activity.attachments && activity.attachments.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {activity.attachments.map((att, i) => {
            const url = filePublicUrl(att.url);
            const label = att.originalName || att.fileName || 'Attachment';
            return (
              <a
                key={att._id || i}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors text-xs font-medium max-w-[220px]"
              >
                <HiOutlinePaperClip className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">{label}</span>
              </a>
            );
          })}
        </div>
      )}
      {activity.recordingUrl && (
        <div className="flex items-center gap-2 mt-1">
          <a
            href={activity.recordingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors text-xs font-medium"
          >
            <HiOutlinePlay className="w-3.5 h-3.5" />
            Play Recording
          </a>
        </div>
      )}
    </div>
  );
};

const NoteEntry = ({ activity }) => (
  <div>
    <p className="text-sm text-gray-700 whitespace-pre-wrap">{activity.text}</p>
  </div>
);

const AssignmentEntry = ({ activity }) => {
  const statusLabel = activity.status === 'active' ? 'Assigned' : activity.status === 'transferred' ? 'Transferred' : 'Completed';
  const statusColor = activity.status === 'active' ? 'text-purple-700' : 'text-gray-500';

  return (
    <div className="space-y-1">
      <p className="text-sm text-gray-700">
        <span className={`font-medium ${statusColor}`}>{statusLabel}</span> to{' '}
        <span className="font-medium text-gray-900">{activity.assignedToName}</span>
      </p>
      {activity.unassignedAt && (
        <p className="text-xs text-gray-400">
          Unassigned on {format(new Date(activity.unassignedAt), 'MMM d, yyyy h:mm a')}
        </p>
      )}
    </div>
  );
};

const AttachmentEntry = ({ activity }) => {
  const url = filePublicUrl(activity.url);
  const isImage = activity.mimeType?.startsWith('image/');

  return (
    <div className="flex items-center gap-2">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors text-sm font-medium"
      >
        <HiOutlinePaperClip className="w-3.5 h-3.5" />
        {activity.fileName || 'Attachment'}
      </a>
      {activity.size && (
        <span className="text-xs text-gray-400">
          {(activity.size / 1024).toFixed(1)} KB
        </span>
      )}
    </div>
  );
};

const CreatedEntry = ({ activity }) => (
  <div>
    <p className="text-sm text-gray-700">
      Lead created{activity.source ? ` from ${activity.source}` : ''}{activity.createdByName ? ` by ${activity.createdByName}` : ''}
    </p>
  </div>
);

const FOLLOWUP_STATUS_CONFIG = {
  pending: { label: 'Scheduled', color: 'bg-yellow-100 text-yellow-700', icon: HiOutlineCalendar },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: HiOutlineCheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500', icon: HiOutlineXCircle },
};

const FOLLOWUP_TYPE_LABELS = {
  call: 'Phone Call',
  email: 'Email',
  visit: 'Visit',
  meeting: 'Meeting',
  other: 'Follow-up',
};

const FollowUpEntry = ({ activity }) => {
  const statusConfig = FOLLOWUP_STATUS_CONFIG[activity.status] || FOLLOWUP_STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;
  const typeLabel = FOLLOWUP_TYPE_LABELS[activity.followUpType] || 'Follow-up';

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusConfig.color}`}>
          {statusConfig.label}
        </span>
        <span className="text-xs text-gray-500">{typeLabel}</span>
      </div>
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <HiOutlineCalendar className="w-3.5 h-3.5" />
        <span>Scheduled: {format(new Date(activity.scheduledDate), 'MMM d, yyyy h:mm a')}</span>
      </div>
      {activity.completedAt && (
        <div className="flex items-center gap-2 text-xs text-green-600">
          <HiOutlineCheckCircle className="w-3.5 h-3.5" />
          <span>Completed: {format(new Date(activity.completedAt), 'MMM d, yyyy h:mm a')}</span>
        </div>
      )}
      {activity.assignedToName && (
        <p className="text-xs text-gray-500">Assigned to {activity.assignedToName}</p>
      )}
      {activity.escalated && (
        <div className="flex items-center gap-1 text-xs text-red-600">
          <HiOutlineExclamation className="w-3.5 h-3.5" />
          <span className="font-medium">Escalated</span>
        </div>
      )}
      {activity.notes && (
        <p className="text-sm text-gray-700 mt-1">{activity.notes}</p>
      )}
    </div>
  );
};

const ACTIVITY_RENDERERS = {
  call: CallEntry,
  note: NoteEntry,
  assignment: AssignmentEntry,
  attachment: AttachmentEntry,
  created: CreatedEntry,
  followup: FollowUpEntry,
};

const LeadActivityTimeline = ({ leadId }) => {
  const { data: activityData, isLoading, error } = useQuery({
    queryKey: ['leadActivity', leadId],
    queryFn: async () => {
      const response = await leadsAPI.getActivity(leadId, { limit: 100 });
      // Handle both { data: [...] } and direct array responses
      const result = response.data;
      return Array.isArray(result) ? result : (result?.data || []);
    },
    enabled: !!leadId,
  });

  const activities = activityData || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10 text-gray-400">
        <svg className="animate-spin w-5 h-5 mr-2" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        Loading activity...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-red-500">Failed to load activity timeline</p>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="text-center py-10">
        <HiOutlinePencil className="w-10 h-10 text-gray-300 mx-auto mb-2" />
        <p className="text-sm text-gray-500 font-medium">No activity yet</p>
        <p className="text-xs text-gray-400 mt-1">Calls, notes, and assignments will appear here</p>
      </div>
    );
  }

  // Group activities by date
  const grouped = [];
  let currentDate = null;

  for (const activity of activities) {
    const date = format(new Date(activity.timestamp), 'MMMM d, yyyy');
    if (date !== currentDate) {
      currentDate = date;
      grouped.push({ type: 'date', date, timestamp: activity.timestamp });
    }
    grouped.push(activity);
  }

  return (
    <div className="flow-root">
      <ul className="-mb-8">
        {grouped.map((item, index) => {
          if (item.type === 'date') {
            return (
              <li key={`date-${index}`} className="relative pb-4">
                <div className="sticky top-0 z-10 flex items-center py-1.5">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider bg-white pr-3">
                    {item.date === format(new Date(), 'MMMM d, yyyy') ? 'Today' : item.date}
                  </span>
                  <div className="flex-1 border-t border-gray-200" />
                </div>
              </li>
            );
          }

          const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.note;
          const Icon = config.icon;
          const EntryComponent = ACTIVITY_RENDERERS[item.type] || NoteEntry;
          const isLast = index === grouped.length - 1;

          return (
            <li key={item.id || `activity-${index}`} className="relative pb-6">
              {/* Timeline connector */}
              {!isLast && (
                <div className="absolute left-4 top-8 bottom-0 w-0.5 bg-gray-200" />
              )}

              <div className="relative flex gap-x-4">
                {/* Icon */}
                <div className={`relative flex h-8 w-8 flex-none items-center justify-center rounded-full ${config.bgColor} ring-4 ring-white`}>
                  <Icon className={`h-4 w-4 ${config.textColor}`} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pt-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-semibold ${config.textColor}`}>
                      {config.label}
                    </span>
                    <span className="text-xs text-gray-400">
                      {format(new Date(item.timestamp), 'h:mm a')}
                    </span>
                  </div>
                  <EntryComponent activity={item} />
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default LeadActivityTimeline;